/**
 * Disclaimer modal: auto-opens site-wide until the visitor agrees.
 * Consent is persisted in localStorage with a per-site, versioned key.
 */
import {
	lockBackgroundScroll,
	unlockBackgroundScroll,
} from '../shared/background-scroll-lock';

( function () {
	'use strict';

	const OPEN_CLASS = 'is-open';
	const PENDING_CLASS = 'alteia-disclaimer-pending';
	const CONSENT_VALUE = '1';
	const FALLBACK_CONSENT_KEY = 'alteia_disclaimer_consent_1_v1';

	/**
	 * @param {HTMLElement} root
	 * @return {HTMLElement[]}
	 */
	function getFocusableElements( root ) {
		const selectors = [
			'a[href]',
			'button:not([disabled])',
			'textarea:not([disabled])',
			'input:not([disabled])',
			'select:not([disabled])',
			'[tabindex]:not([tabindex="-1"])',
		];

		return Array.from(
			root.querySelectorAll( selectors.join( ',' ) )
		).filter(
			( el ) =>
				! el.hasAttribute( 'disabled' ) &&
				! el.getAttribute( 'aria-hidden' )
		);
	}

	/**
	 * @param {HTMLButtonElement|null} button
	 * @param {boolean}                enabled
	 */
	function setAgreeEnabled( button, enabled ) {
		if ( ! button ) {
			return;
		}

		button.disabled = ! enabled;
		button.setAttribute( 'aria-disabled', enabled ? 'false' : 'true' );
	}

	/**
	 * Resolve the versioned consent storage key for this modal instance.
	 *
	 * @param {HTMLElement} modal
	 * @return {string}
	 */
	function getConsentKey( modal ) {
		const key = modal.getAttribute( 'data-consent-key' );
		return key && key.trim() ? key.trim() : FALLBACK_CONSENT_KEY;
	}

	/**
	 * @param {string} key
	 */
	function setConsent( key ) {
		try {
			window.localStorage.setItem( key, CONSENT_VALUE );
		} catch {
			// localStorage unavailable (e.g. private browsing quota errors).
		}
	}

	/**
	 * @param {string} key
	 * @return {boolean}
	 */
	function hasConsent( key ) {
		try {
			return window.localStorage.getItem( key ) === CONSENT_VALUE;
		} catch {
			return false;
		}
	}

	/**
	 * Clear the early FOUC lock applied by the head bootstrap script.
	 */
	function clearPendingState() {
		document.documentElement.classList.remove( PENDING_CLASS );
	}

	/**
	 * @param {HTMLElement} modal Modal root element.
	 */
	function initDisclaimerModal( modal ) {
		const dialog = modal.querySelector(
			'.alteia-disclaimer-modal__dialog'
		);
		const closeBtn = modal.querySelector(
			'.alteia-disclaimer-modal__close'
		);
		const agreeBtn = /** @type {HTMLButtonElement|null} */ (
			modal.querySelector( '.alteia-disclaimer-modal__agree' )
		);
		const body = modal.querySelector( '.alteia-disclaimer-modal__body' );
		const consentKey = getConsentKey( modal );
		let lastActiveElement = null;

		/**
		 * Enable Agree only after user scrolls to bottom.
		 */
		function updateAgreeState() {
			if ( ! body ) {
				setAgreeEnabled( agreeBtn, true );
				return;
			}

			const atBottom =
				body.scrollTop + body.clientHeight >= body.scrollHeight - 2;
			setAgreeEnabled( agreeBtn, atBottom );
		}

		/**
		 * Open the modal.
		 */
		function openModal() {
			lastActiveElement = document.activeElement;
			modal.hidden = false;
			modal.classList.add( OPEN_CLASS );
			lockBackgroundScroll();
			clearPendingState();

			// Reset scroll and disable Agree until content is read.
			if ( body ) {
				body.scrollTop = 0;
			}
			setAgreeEnabled( agreeBtn, false );
			updateAgreeState();

			const focusTarget = closeBtn || agreeBtn;
			if ( focusTarget ) {
				focusTarget.focus();
			}
		}

		/**
		 * Close the modal and persist consent for this site + version.
		 */
		function closeModal() {
			setConsent( consentKey );
			clearPendingState();
			modal.classList.remove( OPEN_CLASS );
			unlockBackgroundScroll();

			window.setTimeout( () => {
				modal.hidden = true;
				if (
					lastActiveElement &&
					typeof lastActiveElement.focus === 'function'
				) {
					lastActiveElement.focus();
				}
			}, 300 );
		}

		if ( closeBtn ) {
			closeBtn.addEventListener( 'click', closeModal );
		}

		if ( agreeBtn ) {
			agreeBtn.addEventListener( 'click', closeModal );
		}

		document.addEventListener( 'keydown', ( event ) => {
			if ( ! modal.classList.contains( OPEN_CLASS ) ) {
				return;
			}

			// Trap focus within the dialog while open.
			if ( event.key === 'Tab' && dialog ) {
				const focusables = getFocusableElements( dialog );
				if ( focusables.length === 0 ) {
					return;
				}

				const first = focusables[ 0 ];
				const last = focusables[ focusables.length - 1 ];
				const active = document.activeElement;

				if ( event.shiftKey && active === first ) {
					event.preventDefault();
					last.focus();
				} else if ( ! event.shiftKey && active === last ) {
					event.preventDefault();
					first.focus();
				}
			}
		} );

		if ( dialog ) {
			dialog.addEventListener( 'click', ( event ) => {
				event.stopPropagation();
			} );
		}

		if ( body ) {
			body.addEventListener( 'scroll', updateAgreeState, {
				passive: true,
			} );
		}

		window.addEventListener( 'resize', () => {
			if ( modal.classList.contains( OPEN_CLASS ) ) {
				updateAgreeState();
			}
		} );

		// Auto-open until consent is stored for this site + version.
		if ( hasConsent( consentKey ) ) {
			clearPendingState();
			return;
		}

		openModal();
	}

	document.addEventListener( 'DOMContentLoaded', () => {
		const modals = document.querySelectorAll( '.alteia-disclaimer-modal' );

		if ( ! modals.length ) {
			clearPendingState();
			return;
		}

		modals.forEach( initDisclaimerModal );
	} );
} )();
